import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import './ui.css';

const ToastContext = createContext(null);
const SHOWN_FOR = 3000;

/**
 * After something is saved, sent or deleted: one short line naming what
 * happened ("Session saved"), at the bottom, gone after 3 seconds. Problems
 * never go in a toast; they stay on the form or page until fixed.
 *
 *   const toast = useToast();
 *   toast('Session saved');
 */
export function ToastProvider({ children }) {
  const [message, setMessage] = useState(null);
  const [key, setKey] = useState(0);
  const timer = useRef(null);

  const toast = useCallback((text) => {
    clearTimeout(timer.current);
    setMessage(text);
    setKey(k => k + 1);
    timer.current = setTimeout(() => setMessage(null), SHOWN_FOR);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div role="status" aria-live="polite" className="ui-sr-only-wrap">
          {message && (
            <div key={key} className="ui-toast">
              <CheckCircleIcon aria-hidden="true" />
              <span>{message}</span>
            </div>
          )}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// Outside the provider (some tests) a toast does nothing
export function useToast() {
  return useContext(ToastContext) || noToast;
}

function noToast() {}
